import { DEFAULT_LOCALE } from '@/lib/i18n';
import { getPrimaryGameImage } from '@/lib/game-image';

function cloneSearchParams(qs: URLSearchParams): URLSearchParams {
  return new URLSearchParams(qs.toString());
}

/** Strapi v5 returns empty sets for missing localizations — fall back to English explicitly. */
function withEnglishLocale(qs: URLSearchParams): URLSearchParams {
  const fallback = cloneSearchParams(qs);
  fallback.set('locale', DEFAULT_LOCALE);
  return fallback;
}

export const getCmsBaseUrl = () => {
  const trim = (value: string) => value.replace(/\/+$/, '');
  const internalUrl = String(
    process.env.CMS_URL_INTERNAL ?? process.env.CMS_URL ?? '',
  ).trim();
  if (typeof window === 'undefined' && internalUrl) {
    return trim(internalUrl);
  }
  const publicUrl = String(process.env.NEXT_PUBLIC_CMS_URL ?? '').trim();
  if (publicUrl) {
    return trim(publicUrl);
  }
  return typeof window === 'undefined' ? 'http://127.0.0.1:1338' : 'http://localhost:1338';
};

export type CmsGameEntry = {
  id: number;
  documentId?: string | null;
  title?: string | null;
  slug?: string | null;
  demoUrl?: string | null;
  rank?: string | null;
  rtp?: string | null;
  volatility?: string | null;
  maxWin?: string | null;
  description?: string | null;
  isActive?: boolean | null;
  gameProvider?: { id?: number; name?: string | null; slug?: string | null } | null;
  image?: CmsMedia | null;
  releaseDate?: string | null;
  type?: string | null;
  hitFrequency?: string | null;
  minBet?: string | null;
  maxBet?: string | null;
  layout?: string | null;
  betways?: string | null;
  features?: string | null;
  theme?: string | null;
  otherTags?: string | null;
  technology?: string | null;
  gameSize?: string | null;
  lastUpdate?: string | null;
};

/** Normalized game for list/detail pages */
export type GameForFrontend = {
  id: string;
  slug: string;
  title: string;
  provider: string;
  providerSlug: string;
  demoUrl: string;
  image: string;
  rank: string;
  rtp: string;
  volatility: string;
  maxWin: string;
  description?: string | null;
  releaseDate?: string | null;
  type?: string | null;
  hitFrequency?: string | null;
  minBet?: string | null;
  maxBet?: string | null;
  layout?: string | null;
  betways?: string | null;
  features?: string | null;
  theme?: string | null;
  otherTags?: string | null;
  technology?: string | null;
  gameSize?: string | null;
};

export type GameProviderEntry = {
  id: number;
  documentId?: string | null;
  name?: string | null;
  slug?: string | null;
  description?: string | null;
  logo?: CmsMedia | null;
  isActive?: boolean | null;
  sortOrder?: number | null;
  established?: number | null;
  gamesCount?: number | null;
  jurisdictions?: string | null;
  newsCount?: number | null;
  promotionsCount?: number | null;
  studioUrl?: string | null;
};

export type BlogCategoryRef = {
  id: number;
  name?: string | null;
  slug?: string | null;
};

export type BlogAuthorRef = {
  id: number;
  name?: string | null;
  slug?: string | null;
  avatar?: CmsMedia | null;
  bio?: string | null;
  expertise?: string | null;
  certificates?: unknown;
  socialLinks?: unknown;
};

export type BlogFaqItem = {
  id?: number;
  question: string;
  answer: string;
};

export type ArticleEntry = {
  id: number;
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
  featured?: boolean | null;
  tags?: string[];
  coverImage?: CmsMedia | null;
  category?: BlogCategoryRef | null;
  author?: Pick<BlogAuthorRef, 'id' | 'name' | 'slug'> | null;
};

export type BlogArticleDetail = ArticleEntry & {
  metaTitle?: string | null;
  metaDescription?: string | null;
  tags?: string[];
  faq?: BlogFaqItem[];
  linkedCasinos?: Array<{
    id: number;
    name?: string | null;
    slug?: string | null;
    rating?: number | string | null;
    shortDescription?: string | null;
    logo?: CmsMedia | null;
  }>;
  relatedArticles?: Array<{
    id: number;
    title: string;
    slug: string;
    excerpt?: string | null;
  }>;
  readingTimeMinutes?: number;
};

export type StaticPageEntry = {
  id: number;
  title: string;
  slug: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  blocks?: unknown[] | null;
  content?: string | null;
};

export type CmsCountry = {
  id: number;
  name?: string | null;
  code?: string | null;
  slug?: string | null;
};

export type CmsReference = {
  id: number;
  name?: string | null;
  slug?: string | null;
};

export type CmsMedia = {
  url?: string | null;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
};

export type CmsProConItem = {
  id: number;
  text?: string | null;
};

export type CmsCasino = {
  id: number;
  documentId?: string | null;
  name?: string | null;
  slug?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  longDescription?: string | null;
  welcomeBonus?: string | null;
  minDeposit?: number | string | null;
  minWithdrawal?: number | string | null;
  withdrawalTime?: string | null;
  bonusScore?: number | string | null;
  paymentsScore?: number | string | null;
  uxScore?: number | string | null;
  rating?: number | string | null;
  isActive?: boolean | null;
  isFeatured?: boolean | null;
  websiteUrl?: string | null;
  affiliateUrl?: string | null;
  foundedYear?: number | null;
  owner?: string | null;
  supportEmail?: string | null;
  supportLiveChat?: boolean | null;
  supportPhone?: string | null;
  games?: unknown;
  bonuses?: unknown;
  gameTypes?: unknown;
  topSlots?: unknown;
  paymentMethodsDetails?: unknown;
  licensesDetails?: unknown;
  casinoAttributes?: unknown;
  scannedCountries?: unknown;
  sourceUrl?: string | null;
  sourceRating?: number | string | null;
  sourceVotes?: number | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaKeywords?: string | null;
  country?: CmsCountry | null;
  paymentMethods?: CmsReference[] | null;
  licenses?: Array<CmsReference & { url?: string | null }> | null;
  withdrawalMethods?: CmsReference[] | null;
  casinoTypes?: CmsReference[] | null;
  softwareProviders?: CmsReference[] | null;
  vipProgram?: CmsReference | null;
  bestForPlayersFrom?: CmsCountry[] | null;
  languages?: Array<CmsReference & { code?: string | null }> | null;
  currencies?: Array<CmsReference & { code?: string | null; symbol?: string | null }> | null;
  logo?: CmsMedia | null;
  banner?: CmsMedia | null;
  screenshots?: CmsMedia[] | null;
  pros?: CmsProConItem[] | null;
  cons?: CmsProConItem[] | null;
};

type StrapiResponse<T> = {
  data: Array<
    | {
        id: number;
        attributes: T;
      }
    | (T & { id: number })
  >;
};

const normalizeEntry = <T>(entry: StrapiResponse<T>['data'][number]): T & { id: number } => {
  if ('attributes' in entry) {
    return { id: entry.id, ...(entry.attributes as T) };
  }
  return entry as T & { id: number };
};

type StrapiFindResponse<T> = {
  data?: T[] | { results?: T[]; pagination?: { page: number; pageSize: number; pageCount: number; total: number } };
  meta?: { pagination?: { page: number; pageSize: number; pageCount: number; total: number } };
};

function extractCasinoItems(json: StrapiFindResponse<CmsCasino>): CmsCasino[] {
  const data = json.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: CmsCasino[] }).results;
  }
  return [];
}

function extractCasinoPagination(json: StrapiFindResponse<CmsCasino>) {
  const meta = json.meta?.pagination;
  if (meta) return meta;
  const data = json.data;
  if (data && typeof data === 'object' && !Array.isArray(data) && data.pagination) return data.pagination;
  return null;
}

function mapStrapi5Provider(raw: Record<string, unknown>, index: number): GameProviderEntry {
  const logo = raw.logo;
  let logoUrl: string | null = null;
  if (logo && typeof logo === 'object') {
    const L = logo as Record<string, unknown>;
    const data = L.data as Record<string, unknown> | undefined;
    const attrs = data?.attributes as Record<string, unknown> | undefined;
    logoUrl = (L.url as string) ?? (attrs?.url as string) ?? null;
  }
  return {
    id: (raw.id as number) ?? index,
    documentId: (raw.documentId as string) ?? null,
    name: (raw.name as string) ?? null,
    slug: (raw.slug as string) ?? null,
    description: (raw.description as string) ?? null,
    logo: logoUrl ? { url: logoUrl } : null,
    isActive: raw.isActive as boolean | undefined,
    sortOrder: (raw.sortOrder as number) ?? null,
    established: typeof raw.established === 'number' ? raw.established : null,
    gamesCount: typeof raw.gamesCount === 'number' ? raw.gamesCount : null,
    jurisdictions: (raw.jurisdictions as string) ?? null,
    newsCount: typeof raw.newsCount === 'number' ? raw.newsCount : null,
    promotionsCount: typeof raw.promotionsCount === 'number' ? raw.promotionsCount : null,
    studioUrl: (raw.studioUrl as string) ?? null,
  };
}

function sortProviders(items: GameProviderEntry[]): GameProviderEntry[] {
  return items.slice().sort((a, b) => {
    const sortA = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
    const sortB = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
    if (sortA !== sortB) return sortA - sortB;
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

async function fetchGameProvidersInternal(options: {
  locale?: string;
  limit?: number;
  withGamesOnly?: boolean;
}): Promise<GameProviderEntry[]> {
  const baseUrl = getCmsBaseUrl();
  const limit = options.limit ?? 1000;
  const locale = options.locale?.trim();
  const withGamesOnly = options.withGamesOnly ?? true;
  const pageSize = 100;
  const all: unknown[] = [];
  let page = 1;
  let hasMore = true;

  try {
    while (hasMore) {
      const qs = new URLSearchParams();
      qs.set('filters[isActive][$eq]', 'true');
      if (withGamesOnly) {
        qs.set('filters[gamesCount][$gt]', '0');
      }
      qs.set('sort', 'sortOrder:asc,name:asc');
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('populate[logo][fields][0]', 'url');
      qs.set('populate[logo][fields][1]', 'alternativeText');
      if (locale) {
        qs.set('locale', locale);
      }
      const url = `${baseUrl}/api/game-providers?${qs.toString()}`;
      const response = await fetch(url, { next: { revalidate: 60 } });
      if (!response.ok) return [];
      const json = (await response.json()) as Record<string, unknown>;
      const data = json.data;
      let raw: unknown[] = [];
      if (Array.isArray(data)) {
        raw = data;
      } else if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
        raw = (data as { results: unknown[] }).results;
      }
      all.push(...raw);
      if (limit != null && all.length >= limit) {
        return all.slice(0, limit).map((item: unknown, i) => mapStrapi5Provider(ensureRecord(item), i));
      }
      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      hasMore = page < pageCount && raw.length === pageSize;
      page++;
    }
    return all.map((item: unknown, i) => mapStrapi5Provider(ensureRecord(item), i));
  } catch {
    return [];
  }
}

export async function fetchGameProviders(options: {
  limit?: number;
  locale?: string;
  withGamesOnly?: boolean;
} = {}): Promise<GameProviderEntry[]> {
  const limit = options.limit ?? 1000;
  const locale = options.locale?.trim().toLowerCase();
  const withGamesOnly = options.withGamesOnly ?? true;

  // For non-default locales, merge localized entries with default locale fallback.
  // This avoids "shrinking" the providers list when only part of content is translated.
  if (locale && locale !== 'en' && ['fr', 'es', 'ru'].includes(locale)) {
    const [localized, fallback] = await Promise.all([
      fetchGameProvidersInternal({ locale, limit, withGamesOnly }),
      fetchGameProvidersInternal({ limit, withGamesOnly }),
    ]);

    const keyOf = (item: GameProviderEntry) =>
      String(item.documentId ?? item.slug ?? item.id);
    const merged = new Map<string, GameProviderEntry>();

    for (const item of fallback) {
      merged.set(keyOf(item), item);
    }
    for (const item of localized) {
      merged.set(keyOf(item), item);
    }

    return sortProviders(Array.from(merged.values())).slice(0, limit);
  }

  return fetchGameProvidersInternal({ locale, limit, withGamesOnly });
}

export async function fetchGameProvidersSearch(q: string, limit = 5): Promise<GameProviderEntry[]> {
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[isActive][$eq]', 'true');
  qs.set('filters[name][$containsi]', q);
  qs.set('sort', 'name:asc');
  qs.set('pagination[pageSize]', String(limit));
  qs.set('populate[logo][fields][0]', 'url');
  const url = `${baseUrl}/api/game-providers?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const raw: unknown[] = Array.isArray(data)
      ? data
      : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
        ? (data as { results: unknown[] }).results
        : [];
    return raw.map((item: unknown, i) => mapStrapi5Provider(ensureRecord(item), i));
  } catch {
    return [];
  }
}

export async function fetchGamesSearch(q: string, limit = 5): Promise<GameForFrontend[]> {
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[isActive][$eq]', 'true');
  qs.set('filters[title][$containsi]', q);
  qs.set('sort', 'title:asc');
  qs.set('pagination[pageSize]', String(limit));
  qs.set('populate[0]', 'gameProvider');
  qs.set('populate[1]', 'image');
  const url = `${baseUrl}/api/games?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const raw: unknown[] = Array.isArray(data)
      ? data
      : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
        ? (data as { results: unknown[] }).results
        : [];
    return raw.map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item)));
  } catch {
    return [];
  }
}

export async function fetchGameProviderBySlug(
  slug: string,
  locale?: string,
): Promise<GameProviderEntry | null> {
  const baseUrl = getCmsBaseUrl();
  try {
    const qs = new URLSearchParams();
    qs.set('filters[slug][$eq]', slug);
    qs.set('filters[isActive][$eq]', 'true');
    qs.set('pagination[pageSize]', '1');
    qs.set('populate[logo][fields][0]', 'url');
    qs.set('populate[logo][fields][1]', 'alternativeText');
    const loc = String(locale ?? '').trim().toLowerCase();
    if (loc && ['en', 'fr', 'es', 'ru'].includes(loc)) {
      qs.set('locale', loc);
    }

    const fetchOnce = async (searchParams: URLSearchParams) => {
      const url = `${baseUrl}/api/game-providers?${searchParams.toString()}`;
      const response = await fetch(url, { next: { revalidate: 60 } });
      if (!response.ok) return null;
      const json = (await response.json()) as Record<string, unknown>;
      const data = json.data;
      const arr = Array.isArray(data) ? data : (data as { results?: unknown[] })?.results ?? [];
      const first = arr[0];
      return first ? mapStrapi5Provider(ensureRecord(first), 0) : null;
    };

    // 1. Try localized version
    let provider = await fetchOnce(qs);

    // 2. Fallback: English localization when fr/es/ru entry is missing
    if (!provider && loc && loc !== DEFAULT_LOCALE) {
      provider = await fetchOnce(withEnglishLocale(qs));
    }

    return provider;
  } catch {
    return null;
  }
}

function ensureRecord(v: unknown): Record<string, unknown> {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  const a = v as { id?: number; attributes?: Record<string, unknown> };
  if (a?.attributes) return { ...a, ...a.attributes } as Record<string, unknown>;
  return {};
}

export type FetchGamesOptions = {
  /** Filter by game provider slug (e.g. "pragmatic-play", "bgaming") */
  provider?: string;
  /** Max number of games to return (stops pagination early) */
  limit?: number;
};

export type GamesCatalogSortOption = 'hlrank' | 'title-asc' | 'title-desc' | 'rtp-desc' | 'rtp-asc';

export type FetchGamesCatalogOptions = {
  page?: number;
  pageSize?: number;
  providers?: string[];
  types?: string[];
  rtpMin?: string;
  rtpMax?: string;
  volatility?: string;
  sort?: GamesCatalogSortOption;
  preset?: string | null;
  maxResults?: number;
  excludeSlug?: string;
};

export type ProviderCasinoAffiliateUrlsMap = Record<string, string[]>;

function extractRelationItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];
  const record = raw as Record<string, unknown>;
  if (Array.isArray(record.data)) return record.data;
  if (Array.isArray(record.results)) return record.results;
  return [];
}

/**
 * Build provider -> affiliateUrl index for random "Go to casino" links.
 * Include only casinos where websiteUrl and affiliateUrl are both present and different.
 */
export async function fetchCasinoProviderAffiliateUrlsMapFromCms(): Promise<ProviderCasinoAffiliateUrlsMap> {
  const baseUrl = getCmsBaseUrl();
  const fetchOpts = buildCmsFetchOptions();
  const pageSize = 200;
  let page = 1;
  let safety = 0;
  const map = new Map<string, Set<string>>();

  try {
    while (true) {
      const qs = new URLSearchParams();
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('filters[isActive][$eq]', 'true');
      qs.set('fields[0]', 'websiteUrl');
      qs.set('fields[1]', 'affiliateUrl');
      qs.set('populate[softwareProviders][fields][0]', 'slug');
      const url = `${baseUrl}/api/casinos?${qs.toString()}`;
      const response = await fetch(url, fetchOpts);
      if (!response.ok) break;

      const json = (await response.json()) as Record<string, unknown>;
      const data = json.data;
      const items = Array.isArray(data)
        ? data
        : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
          ? (data as { results: unknown[] }).results
          : [];

      for (const item of items) {
        const casino = ensureRecord(item);
        const websiteUrl = String(casino.websiteUrl ?? '').trim();
        const affiliateUrl = String(casino.affiliateUrl ?? '').trim();
        if (!websiteUrl || !affiliateUrl || websiteUrl === affiliateUrl) continue;

        const providers = extractRelationItems(casino.softwareProviders);
        for (const providerItem of providers) {
          const provider = ensureRecord(providerItem);
          const providerSlug = String(provider.slug ?? '').trim();
          if (!providerSlug) continue;
          if (!map.has(providerSlug)) {
            map.set(providerSlug, new Set());
          }
          map.get(providerSlug)?.add(affiliateUrl);
        }
      }

      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      if (page >= pageCount) break;
      page += 1;
      safety += 1;
      if (safety > 100) break;
    }
  } catch {
    return {};
  }

  return Object.fromEntries(
    Array.from(map.entries()).map(([providerSlug, urls]) => [providerSlug, Array.from(urls)]),
  );
}

/** Provider slugs linked to active casinos for the given CMS country name. */
export async function fetchProviderSlugsForCountryFromCms(country: string): Promise<string[]> {
  const countryName = String(country ?? '').trim();
  if (!countryName) return [];

  const baseUrl = getCmsBaseUrl();
  const fetchOpts = buildCmsFetchOptions();
  const pageSize = 200;
  let page = 1;
  let safety = 0;
  const slugs = new Set<string>();

  try {
    while (true) {
      const qs = new URLSearchParams();
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('filters[isActive][$eq]', 'true');
      qs.set('filters[country][name][$eq]', countryName);
      qs.set('populate[softwareProviders][fields][0]', 'slug');

      const response = await fetch(`${baseUrl}/api/casinos?${qs.toString()}`, fetchOpts);
      if (!response.ok) break;

      const json = (await response.json()) as Record<string, unknown>;
      const data = json.data;
      const items = Array.isArray(data)
        ? data
        : (data &&
            typeof data === 'object' &&
            Array.isArray((data as { results?: unknown[] }).results))
          ? (data as { results: unknown[] }).results
          : [];

      for (const item of items) {
        const casino = ensureRecord(item);
        for (const providerItem of extractRelationItems(casino.softwareProviders)) {
          const provider = ensureRecord(providerItem);
          const slug = String(provider.slug ?? '').trim();
          if (slug) slugs.add(slug);
        }
      }

      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      if (page >= pageCount) break;
      page += 1;
      safety += 1;
      if (safety > 100) break;
    }
  } catch {
    return [];
  }

  return Array.from(slugs).sort((a, b) => a.localeCompare(b));
}

/**
 * Fetch affiliate URLs for one provider from casinos where websiteUrl differs from affiliateUrl.
 */
export async function fetchCasinoAffiliateUrlsForProviderSlug(
  providerSlug: string,
): Promise<string[]> {
  const normalizedProviderSlug = String(providerSlug ?? '').trim();
  if (!normalizedProviderSlug) return [];

  const baseUrl = getCmsBaseUrl();
  const fetchOpts = buildCmsFetchOptions();
  const pageSize = 200;
  let page = 1;
  let safety = 0;
  const urls = new Set<string>();

  try {
    while (true) {
      const qs = new URLSearchParams();
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('filters[isActive][$eq]', 'true');
      qs.set('filters[softwareProviders][slug][$eq]', normalizedProviderSlug);
      qs.set('fields[0]', 'websiteUrl');
      qs.set('fields[1]', 'affiliateUrl');

      const response = await fetch(`${baseUrl}/api/casinos?${qs.toString()}`, fetchOpts);
      if (!response.ok) break;

      const json = (await response.json()) as Record<string, unknown>;
      const data = json.data;
      const items = Array.isArray(data)
        ? data
        : (data &&
            typeof data === 'object' &&
            Array.isArray((data as { results?: unknown[] }).results))
          ? (data as { results: unknown[] }).results
          : [];

      for (const item of items) {
        const casino = ensureRecord(item);
        const websiteUrl = String(casino.websiteUrl ?? '').trim();
        const affiliateUrl = String(casino.affiliateUrl ?? '').trim();
        if (websiteUrl && affiliateUrl && websiteUrl !== affiliateUrl) {
          urls.add(affiliateUrl);
        }
      }

      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      if (page >= pageCount) break;
      page += 1;
      safety += 1;
      if (safety > 100) break;
    }
  } catch {
    return [];
  }

  return Array.from(urls);
}

/** Fetches a single page of games (for client-side / API pagination) */
export async function fetchGamesPage(
  page = 1,
  pageSize = 500,
  options?: { provider?: string }
): Promise<{ games: GameForFrontend[]; pageCount: number }> {
  const baseUrl = getCmsBaseUrl();
  const providerSlug = options?.provider?.trim();
  const qs = new URLSearchParams();
  qs.set('filters[isActive][$eq]', 'true');
  if (providerSlug) {
    qs.set('filters[gameProvider][slug][$eq]', providerSlug);
  }
  qs.set('sort', 'title:asc');
  qs.set('pagination[page]', String(page));
  qs.set('pagination[pageSize]', String(pageSize));
  qs.set('populate[0]', 'gameProvider');
  qs.set('populate[1]', 'image');
  const url = `${baseUrl}/api/games?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return { games: [], pageCount: 0 };
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const raw: unknown[] = Array.isArray(data)
      ? data
      : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
        ? (data as { results: unknown[] }).results
        : [];
    const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
    const pageCount = meta?.pagination?.pageCount ?? 1;
    return {
      games: raw.map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item))),
      pageCount,
    };
  } catch {
    return { games: [], pageCount: 0 };
  }
}

export async function fetchGames(options?: FetchGamesOptions): Promise<GameForFrontend[]> {
  const baseUrl = getCmsBaseUrl();
  const pageSize = 100;
  const all: unknown[] = [];
  let page = 1;
  let hasMore = true;
  const providerSlug = options?.provider?.trim();
  const limit = options?.limit;

  try {
    while (hasMore) {
      const qs = new URLSearchParams();
      qs.set('filters[isActive][$eq]', 'true');
      if (providerSlug) {
        qs.set('filters[gameProvider][slug][$eq]', providerSlug);
      }
      qs.set('sort', 'title:asc');
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('populate[0]', 'gameProvider');
      qs.set('populate[1]', 'image');
      const url = `${baseUrl}/api/games?${qs.toString()}`;
      const response = await fetch(url, { next: { revalidate: 60 } });
      if (!response.ok) return [];
      const json = (await response.json()) as Record<string, unknown>;
      const data = json.data;
      const raw: unknown[] = Array.isArray(data)
        ? data
        : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
          ? (data as { results: unknown[] }).results
          : [];
      all.push(...raw);
      if (limit != null && all.length >= limit) {
        return all.slice(0, limit).map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item)));
      }
      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      hasMore = page < pageCount && raw.length === pageSize;
      page++;
    }
    return all.map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item)));
  } catch {
    return [];
  }
}

/** Games with releaseDate > today, sorted by releaseDate asc (closest first), limit 10 */
export async function fetchComingSoonGames(limit = 10): Promise<GameForFrontend[]> {
  const today = new Date().toISOString().slice(0, 10);
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[isActive][$eq]', 'true');
  qs.set('filters[releaseDate][$gt]', today);
  qs.set('sort', 'releaseDate:asc');
  qs.set('pagination[pageSize]', String(limit));
  qs.set('populate[0]', 'gameProvider');
  qs.set('populate[1]', 'image');
  const url = `${baseUrl}/api/games?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const raw: unknown[] = Array.isArray(data)
      ? data
      : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
        ? (data as { results: unknown[] }).results
        : [];
    return raw.map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item)));
  } catch {
    return [];
  }
}

export async function fetchGamesBySlugs(slugs: string[]): Promise<GameForFrontend[]> {
  const valid = slugs.filter((s) => typeof s === 'string' && s.trim());
  if (valid.length === 0) return [];

  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[isActive][$eq]', 'true');
  valid.forEach((s, i) => qs.set(`filters[slug][$in][${i}]`, s.trim()));
  qs.set('pagination[pageSize]', String(Math.min(valid.length, 100)));
  qs.set('populate[0]', 'gameProvider');
  qs.set('populate[1]', 'image');
  const url = `${baseUrl}/api/games?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const raw: unknown[] = Array.isArray(data)
      ? data
      : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
        ? (data as { results: unknown[] }).results
        : [];
    return raw.map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item)));
  } catch {
    return [];
  }
}

/** Fetches games from CMS by exact title match. Returns games in the same order as titles (when matched). */
export async function fetchGamesByTitles(titles: string[]): Promise<GameForFrontend[]> {
  const valid = titles.filter((t) => typeof t === 'string' && t.trim());
  if (valid.length === 0) return [];

  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[isActive][$eq]', 'true');
  valid.forEach((t, i) => qs.set(`filters[title][$in][${i}]`, t.trim()));
  qs.set('pagination[pageSize]', String(Math.min(valid.length, 100)));
  qs.set('populate[0]', 'gameProvider');
  qs.set('populate[1]', 'image');
  const url = `${baseUrl}/api/games?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const raw: unknown[] = Array.isArray(data)
      ? data
      : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results))
        ? (data as { results: unknown[] }).results
        : [];
    const games = raw.map((item: unknown) => mapCmsGameToFrontend(normalizeGameEntry(item)));
    // Preserve order of requested titles (first occurrence wins for duplicates)
    const order = new Map(valid.map((t, i) => [t.trim().toLowerCase(), i]));
    return games.slice().sort((a, b) => {
      const ia = order.get(a.title.trim().toLowerCase()) ?? 999;
      const ib = order.get(b.title.trim().toLowerCase()) ?? 999;
      return ia - ib;
    });
  } catch {
    return [];
  }
}

export async function fetchGameBySlug(slug: string): Promise<GameForFrontend | null> {
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[slug][$eq]', slug);
  qs.set('filters[isActive][$eq]', 'true');
  qs.set('populate[0]', 'gameProvider');
  qs.set('populate[1]', 'image');
  const url = `${baseUrl}/api/games?${qs.toString()}`;
  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return null;
    const json = (await response.json()) as Record<string, unknown>;
    const data = json.data;
    const arr = Array.isArray(data) ? data : (data as { results?: unknown[] })?.results ?? [];
    const first = arr[0];
    return first ? mapCmsGameToFrontend(normalizeGameEntry(first)) : null;
  } catch {
    return null;
  }
}

function normalizeGameEntry(raw: unknown): CmsGameEntry & { id: number } {
  const r = ensureRecord(raw);
  if (r.id != null && r.attributes && typeof r.attributes === 'object') {
    return { id: r.id as number, ...(r.attributes as Record<string, unknown>) } as CmsGameEntry & { id: number };
  }
  let id: number;
  if (typeof r.id === 'number') {
    id = r.id;
  } else if (r.documentId) {
    id = Math.abs(String(r.documentId).split('').reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0));
  } else {
    id = 0;
  }
  return { id, ...r } as CmsGameEntry & { id: number };
}

function mapCmsGameToFrontend(g: CmsGameEntry & { id: number }): GameForFrontend {
  const gp = g.gameProvider;
  const provider =
    typeof gp === 'object' && gp !== null
      ? ((gp as { name?: string }).name ?? (gp as { attributes?: { name?: string } }).attributes?.name ?? '')
      : '';
  const providerSlug =
    typeof gp === 'object' && gp !== null
      ? ((gp as { slug?: string }).slug ?? (gp as { attributes?: { slug?: string } }).attributes?.slug ?? '')
      : '';
  let imageUrl: string;
  const preferredStorageImage = getPrimaryGameImage(providerSlug, g.slug ?? '', 'card');
  if (preferredStorageImage) {
    imageUrl = preferredStorageImage;
  } else {
    imageUrl = '';
  }
  return {
    id: String(g.id),
    slug: g.slug ?? '',
    title: g.title ?? '',
    provider,
    providerSlug,
    demoUrl: g.demoUrl ?? '',
    image: imageUrl,
    rank: g.rank ?? '—',
    rtp: g.rtp ?? '—',
    volatility: g.volatility ?? '—',
    maxWin: g.maxWin ?? '—',
    description: g.description ?? null,
    releaseDate: g.releaseDate ?? null,
    type: g.type ?? null,
    hitFrequency: g.hitFrequency ?? null,
    minBet: g.minBet ?? null,
    maxBet: g.maxBet ?? null,
    layout: g.layout ?? null,
    betways: g.betways ?? null,
    features: g.features ?? null,
    theme: g.theme ?? null,
    otherTags: g.otherTags ?? null,
    technology: g.technology ?? null,
    gameSize: g.gameSize ?? null,
  };
}

export async function fetchGamesCatalogPage(
  options: FetchGamesCatalogOptions = {},
): Promise<{ games: GameForFrontend[]; page: number; pageCount: number; total: number }> {
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  const page = Math.max(1, Number.parseInt(String(options.page ?? '1'), 10) || 1);
  const pageSize = Math.min(100, Math.max(1, Number.parseInt(String(options.pageSize ?? '12'), 10) || 12));
  qs.set('page', String(page));
  qs.set('pageSize', String(pageSize));

  (options.providers ?? []).filter(Boolean).forEach((provider) => qs.append('provider', provider));
  (options.types ?? []).filter(Boolean).forEach((type) => qs.append('type', type));

  if (options.rtpMin) qs.set('rtpMin', options.rtpMin);
  if (options.rtpMax) qs.set('rtpMax', options.rtpMax);
  if (options.volatility) qs.set('volatility', options.volatility);
  if (options.sort) qs.set('sort', options.sort);
  if (options.preset) qs.set('preset', options.preset);
  if (typeof options.maxResults === 'number' && Number.isFinite(options.maxResults) && options.maxResults > 0) {
    qs.set('maxResults', String(options.maxResults));
  }
  if (options.excludeSlug) qs.set('excludeSlug', options.excludeSlug);

  const url = `${baseUrl}/api/games-catalog?${qs.toString()}`;

  try {
    const response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) {
      return { games: [], page, pageCount: 0, total: 0 };
    }

    const json = (await response.json()) as {
      games?: unknown[];
      page?: number;
      pageCount?: number;
      total?: number;
    };

    return {
      games: Array.isArray(json.games)
        ? json.games.map((item) => mapCmsGameToFrontend(normalizeGameEntry(item)))
        : [],
      page: Number(json.page ?? page) || page,
      pageCount: Number(json.pageCount ?? 0) || 0,
      total: Number(json.total ?? 0) || 0,
    };
  } catch {
    return { games: [], page, pageCount: 0, total: 0 };
  }
}

function strapiDataArray(json: Record<string, unknown>): unknown[] {
  const data = json.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: unknown[] }).results;
  }
  return [];
}

function mapCmsMediaField(raw: unknown): CmsMedia | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const directUrl = r.url;
  if (typeof directUrl === 'string' && directUrl) {
    return {
      url: directUrl,
      alternativeText: (r.alternativeText as string) ?? null,
      width: typeof r.width === 'number' ? r.width : null,
      height: typeof r.height === 'number' ? r.height : null,
    };
  }
  const data = r.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    const attrs = d.attributes as Record<string, unknown> | undefined;
    const url = (attrs?.url as string) ?? (d.url as string) ?? '';
    if (url) {
      return {
        url,
        alternativeText: (attrs?.alternativeText as string) ?? null,
        width: typeof attrs?.width === 'number' ? attrs.width : null,
        height: typeof attrs?.height === 'number' ? attrs.height : null,
      };
    }
  }
  return null;
}

function relationRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id === 'number' || typeof r.documentId === 'string') {
    const attrs = r.attributes as Record<string, unknown> | undefined;
    if (attrs && typeof attrs === 'object') {
      return { ...r, ...attrs };
    }
    return r;
  }
  const data = r.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return relationRecord(data);
  }
  return null;
}

function parseTagsJson(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((t) => String(t ?? '').trim()).filter(Boolean);
}

function readingTimeFromContent(text: string): number {
  const words = String(text ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function mapBlogCategoryFromRecord(raw: Record<string, unknown>): BlogCategoryRef {
  return {
    id: (raw.id as number) ?? 0,
    name: (raw.name as string) ?? null,
    slug: (raw.slug as string) ?? null,
  };
}

function mapBlogAuthorRefFromRecord(raw: Record<string, unknown>): BlogAuthorRef {
  let id = typeof raw.id === 'number' ? raw.id : 0;
  if (!id && typeof raw.documentId === 'string') {
    id = Math.abs(
      String(raw.documentId)
        .split('')
        .reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0),
    );
  }
  return {
    id,
    name: (raw.name as string) ?? null,
    slug: (raw.slug as string) ?? null,
    avatar: mapCmsMediaField(raw.avatar),
    bio: (raw.bio as string) ?? null,
    expertise: (raw.expertise as string) ?? null,
    certificates: raw.certificates ?? null,
    socialLinks: raw.socialLinks ?? null,
  };
}

function mapCasinoCardFromRecord(raw: Record<string, unknown>) {
  const r = raw.rating;
  const rating = typeof r === 'number' || typeof r === 'string' ? r : null;
  return {
    id: (raw.id as number) ?? 0,
    name: (raw.name as string) ?? null,
    slug: (raw.slug as string) ?? null,
    rating,
    shortDescription: (raw.shortDescription as string) ?? null,
    logo: mapCmsMediaField(raw.logo),
  };
}

function mapFaqItems(raw: unknown): BlogFaqItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, idx) => {
      const r = ensureRecord(item);
      const question = String(r.question ?? '').trim();
      const answer = String(r.answer ?? '').trim();
      if (!question || !answer) return null;
      return {
        id: typeof r.id === 'number' ? r.id : idx,
        question,
        answer,
      };
    })
    .filter(Boolean) as BlogFaqItem[];
}

function mapRelatedArticlesFromComponents(raw: unknown): BlogArticleDetail['relatedArticles'] {
  if (!Array.isArray(raw)) return [];
  const out: NonNullable<BlogArticleDetail['relatedArticles']> = [];
  for (const row of raw) {
    const r = ensureRecord(row);
    const a = relationRecord(r.article);
    if (!a) continue;
    const title = String(a.title ?? '').trim();
    const slug = String(a.slug ?? '').trim();
    if (!slug) continue;
    out.push({
      id: (a.id as number) ?? 0,
      title: title || slug,
      slug,
      excerpt: (a.excerpt as string) ?? null,
    });
  }
  return out;
}

function mapArticleListItem(raw: Record<string, unknown>): ArticleEntry {
  const cat = relationRecord(raw.category);
  const auth = relationRecord(raw.author);
  return {
    id: (raw.id as number) ?? 0,
    title: String(raw.title ?? ''),
    slug: String(raw.slug ?? ''),
    excerpt: (raw.excerpt as string) ?? null,
    content: (raw.content as string) ?? null,
    publishedAt: (raw.publishedAt as string) ?? null,
    updatedAt: (raw.updatedAt as string) ?? null,
    featured: (raw.featured as boolean) ?? null,
    tags: parseTagsJson(raw.tags),
    coverImage: mapCmsMediaField(raw.coverImage),
    category: cat ? mapBlogCategoryFromRecord(cat) : null,
    author: auth
      ? {
          id: (auth.id as number) ?? 0,
          name: (auth.name as string) ?? null,
          slug: (auth.slug as string) ?? null,
        }
      : null,
  };
}

function mapArticleDetail(raw: Record<string, unknown>): BlogArticleDetail {
  const base = mapArticleListItem(raw);
  const casinos = extractRelationItems(raw.linkedCasinos)
    .map((c) => mapCasinoCardFromRecord(relationRecord(c) ?? {}))
    .filter((c) => c.slug);
  return {
    ...base,
    metaTitle: (raw.metaTitle as string) ?? null,
    metaDescription: (raw.metaDescription as string) ?? null,
    tags: parseTagsJson(raw.tags),
    faq: mapFaqItems(raw.faq),
    linkedCasinos: casinos,
    relatedArticles: mapRelatedArticlesFromComponents(raw.relatedArticleLinks),
    readingTimeMinutes: readingTimeFromContent(
      String(raw.content ?? base.excerpt ?? ''),
    ),
  };
}

function buildBlogListPopulateParams(): URLSearchParams {
  const qs = new URLSearchParams();
  qs.set('sort', 'publishedAt:desc');
  qs.set('pagination[pageSize]', '200');
  qs.set('populate[coverImage][fields][0]', 'url');
  qs.set('populate[coverImage][fields][1]', 'alternativeText');
  qs.set('populate[coverImage][fields][2]', 'width');
  qs.set('populate[coverImage][fields][3]', 'height');
  qs.set('populate[category][fields][0]', 'name');
  qs.set('populate[category][fields][1]', 'slug');
  qs.set('populate[author][fields][0]', 'name');
  qs.set('populate[author][fields][1]', 'slug');
  return qs;
}

function buildBlogDetailPopulateParams(): URLSearchParams {
  const qs = new URLSearchParams();
  const detailFields = [
    'title',
    'slug',
    'excerpt',
    'content',
    'featured',
    'tags',
    'publishedAt',
    'updatedAt',
    'createdAt',
    'metaTitle',
    'metaDescription',
  ];
  detailFields.forEach((field, index) => {
    qs.set(`fields[${index}]`, field);
  });
  qs.set('populate[coverImage][fields][0]', 'url');
  qs.set('populate[coverImage][fields][1]', 'alternativeText');
  qs.set('populate[coverImage][fields][2]', 'width');
  qs.set('populate[coverImage][fields][3]', 'height');
  qs.set('populate[category][fields][0]', 'name');
  qs.set('populate[category][fields][1]', 'slug');
  qs.set('populate[author][fields][0]', 'name');
  qs.set('populate[author][fields][1]', 'slug');
  qs.set('populate[author][fields][2]', 'bio');
  qs.set('populate[author][populate][avatar][fields][0]', 'url');
  qs.set('populate[author][populate][avatar][fields][1]', 'alternativeText');
  qs.set('populate[linkedCasinos][fields][0]', 'name');
  qs.set('populate[linkedCasinos][fields][1]', 'slug');
  qs.set('populate[linkedCasinos][fields][2]', 'rating');
  qs.set('populate[linkedCasinos][fields][3]', 'shortDescription');
  qs.set('populate[linkedCasinos][populate][logo][fields][0]', 'url');
  qs.set('populate[linkedCasinos][populate][logo][fields][1]', 'alternativeText');
  qs.set('populate[faq]', 'true');
  qs.set('populate[relatedArticleLinks][populate][article][fields][0]', 'title');
  qs.set('populate[relatedArticleLinks][populate][article][fields][1]', 'slug');
  qs.set('populate[relatedArticleLinks][populate][article][fields][2]', 'excerpt');
  return qs;
}

async function fetchArticlesFromApi(searchParams: URLSearchParams): Promise<ArticleEntry[]> {
  const baseUrl = getCmsBaseUrl();
  const url = `${baseUrl}/api/articles?${searchParams.toString()}`;
  try {
    const response = await fetch(url, buildCmsFetchOptions());
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    return strapiDataArray(json)
      .map((item) => mapArticleListItem(ensureRecord(item)))
      .filter((g) => g.slug);
  } catch {
    return [];
  }
}

export async function fetchBlogArticles(locale?: string): Promise<ArticleEntry[]> {
  const qs = buildBlogListPopulateParams();
  if (locale) qs.set('locale', locale);
  return fetchArticlesFromApi(qs);
}

export async function fetchBlogArticleBySlug(
  slug: string,
  locale?: string,
): Promise<BlogArticleDetail | null> {
  const baseUrl = getCmsBaseUrl();
  const tryFetch = async (loc: string | undefined) => {
    const qs = buildBlogDetailPopulateParams();
    qs.set('filters[slug][$eq]', String(slug ?? '').trim());
    qs.set('pagination[pageSize]', '1');
    if (loc) qs.set('locale', loc);
    const url = `${baseUrl}/api/articles?${qs.toString()}`;
    const response = await fetch(url, buildCmsFetchOptions());
    if (!response.ok) return null;
    const json = (await response.json()) as Record<string, unknown>;
    const arr = strapiDataArray(json);
    const first = arr[0];
    return first ? mapArticleDetail(ensureRecord(first)) : null;
  };

  try {
    if (locale) {
      const hit = await tryFetch(locale);
      if (hit) return hit;
      if (locale !== 'en') {
        return tryFetch(undefined);
      }
    }
    return tryFetch(undefined);
  } catch {
    return null;
  }
}

export async function fetchBlogCategories(locale?: string): Promise<BlogCategoryRef[]> {
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('sort', 'name:asc');
  qs.set('pagination[pageSize]', '100');
  qs.set('fields[0]', 'name');
  qs.set('fields[1]', 'slug');
  if (locale) qs.set('locale', locale);
  try {
    const response = await fetch(`${baseUrl}/api/blog-categories?${qs.toString()}`, buildCmsFetchOptions());
    if (!response.ok) return [];
    const json = (await response.json()) as Record<string, unknown>;
    return strapiDataArray(json)
      .map((item) => mapBlogCategoryFromRecord(ensureRecord(item)))
      .filter((c) => c.slug);
  } catch {
    return [];
  }
}

export async function fetchBlogCategoryBySlug(
  slug: string,
  locale?: string,
): Promise<BlogCategoryRef | null> {
  const baseUrl = getCmsBaseUrl();
  const tryFetch = async (loc: string | undefined) => {
    const qs = new URLSearchParams();
    qs.set('filters[slug][$eq]', String(slug ?? '').trim());
    qs.set('pagination[pageSize]', '1');
    qs.set('fields[0]', 'name');
    qs.set('fields[1]', 'slug');
    if (loc) qs.set('locale', loc);
    const response = await fetch(`${baseUrl}/api/blog-categories?${qs.toString()}`, buildCmsFetchOptions());
    if (!response.ok) return null;
    const json = (await response.json()) as Record<string, unknown>;
    const first = strapiDataArray(json)[0];
    return first ? mapBlogCategoryFromRecord(ensureRecord(first)) : null;
  };
  try {
    if (locale) {
      const hit = await tryFetch(locale);
      if (hit) return hit;
      if (locale !== 'en') return tryFetch(undefined);
    }
    return tryFetch(undefined);
  } catch {
    return null;
  }
}

export async function fetchBlogAuthorBySlug(slug: string): Promise<BlogAuthorRef | null> {
  const baseUrl = getCmsBaseUrl();
  const qs = new URLSearchParams();
  qs.set('filters[slug][$eq]', String(slug ?? '').trim());
  qs.set('pagination[pageSize]', '1');
  qs.set('populate[avatar][fields][0]', 'url');
  qs.set('populate[avatar][fields][1]', 'alternativeText');
  try {
    const response = await fetch(`${baseUrl}/api/blog-authors?${qs.toString()}`, buildCmsFetchOptions());
    if (!response.ok) return null;
    const json = (await response.json()) as Record<string, unknown>;
    const first = strapiDataArray(json)[0];
    return first ? mapBlogAuthorRefFromRecord(ensureRecord(first)) : null;
  } catch {
    return null;
  }
}

export async function fetchBlogArticlesByAuthorSlug(
  authorSlug: string,
  locale?: string,
): Promise<ArticleEntry[]> {
  const slug = String(authorSlug ?? '').trim();
  if (!slug) return [];

  const qs = buildBlogListPopulateParams();
  qs.set('filters[author][slug][$eq]', slug);
  if (locale) qs.set('locale', locale);
  const filtered = await fetchArticlesFromApi(qs);
  if (filtered.length > 0) return filtered;

  // Strapi v5 often ignores relation slug filters on localized articles — match in app code.
  const articles = await fetchBlogArticles(locale);
  return articles.filter((article) => String(article.author?.slug ?? '').trim() === slug);
}

export async function fetchBlogArticlesByCategorySlug(
  categorySlug: string,
  locale?: string,
): Promise<ArticleEntry[]> {
  const qs = buildBlogListPopulateParams();
  qs.set('filters[category][slug][$eq]', String(categorySlug ?? '').trim());
  if (locale) qs.set('locale', locale);
  return fetchArticlesFromApi(qs);
}

export async function fetchPublishedArticleSlugsForLocale(locale: string): Promise<string[]> {
  const baseUrl = getCmsBaseUrl();
  const pageSize = 200;
  let page = 1;
  let safety = 0;
  const slugs: string[] = [];
  try {
    while (true) {
      const qs = new URLSearchParams();
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('fields[0]', 'slug');
      qs.set('locale', locale);
      const url = `${baseUrl}/api/articles?${qs.toString()}`;
      const res = await fetch(url, { ...buildCmsFetchOptions(), cache: 'no-store' });
      if (!res.ok) break;
      const json = (await res.json()) as Record<string, unknown>;
      strapiDataArray(json)
        .map((item) => String(ensureRecord(item).slug ?? '').trim())
        .filter(Boolean)
        .forEach((s) => slugs.push(s));
      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      if (page >= pageCount) break;
      page += 1;
      safety += 1;
      if (safety > 100) break;
    }
  } catch {
    return [];
  }
  return Array.from(new Set(slugs));
}

export async function fetchBlogAuthorSlugsFromCms(): Promise<string[]> {
  const baseUrl = getCmsBaseUrl();
  const pageSize = 200;
  let page = 1;
  let safety = 0;
  const slugs: string[] = [];
  try {
    while (true) {
      const qs = new URLSearchParams();
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('fields[0]', 'slug');
      const url = `${baseUrl}/api/blog-authors?${qs.toString()}`;
      const res = await fetch(url, { ...buildCmsFetchOptions(), cache: 'no-store' });
      if (!res.ok) break;
      const json = (await res.json()) as Record<string, unknown>;
      strapiDataArray(json)
        .map((item) => String(ensureRecord(item).slug ?? '').trim())
        .filter(Boolean)
        .forEach((s) => slugs.push(s));
      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      if (page >= pageCount) break;
      page += 1;
      safety += 1;
      if (safety > 100) break;
    }
  } catch {
    return [];
  }
  return Array.from(new Set(slugs));
}

export async function fetchBlogCategorySlugsForLocale(locale: string): Promise<string[]> {
  const baseUrl = getCmsBaseUrl();
  const pageSize = 200;
  let page = 1;
  let safety = 0;
  const slugs: string[] = [];
  try {
    while (true) {
      const qs = new URLSearchParams();
      qs.set('pagination[page]', String(page));
      qs.set('pagination[pageSize]', String(pageSize));
      qs.set('fields[0]', 'slug');
      qs.set('locale', locale);
      const url = `${baseUrl}/api/blog-categories?${qs.toString()}`;
      const res = await fetch(url, { ...buildCmsFetchOptions(), cache: 'no-store' });
      if (!res.ok) break;
      const json = (await res.json()) as Record<string, unknown>;
      strapiDataArray(json)
        .map((item) => String(ensureRecord(item).slug ?? '').trim())
        .filter(Boolean)
        .forEach((s) => slugs.push(s));
      const meta = json.meta as { pagination?: { pageCount?: number } } | undefined;
      const pageCount = meta?.pagination?.pageCount ?? 1;
      if (page >= pageCount) break;
      page += 1;
      safety += 1;
      if (safety > 100) break;
    }
  } catch {
    return [];
  }
  return Array.from(new Set(slugs));
}

function buildStaticPageQueryParams(slug: string, locale?: string | null): URLSearchParams {
  const qs = new URLSearchParams();
  qs.set('filters[slug][$eq]', String(slug ?? '').trim());
  if (locale) {
    qs.set('locale', locale);
  }
  qs.set('populate[blocks][on][shared.rich-text]', 'true');
  qs.set('populate[blocks][on][shared.table]', 'true');
  qs.set(
    'populate[blocks][on][shared.table][populate][header][populate][cells]',
    'true',
  );
  qs.set(
    'populate[blocks][on][shared.table][populate][rows][populate][cells]',
    'true',
  );
  return qs;
}

function staticPageHasRenderableContent(page: StaticPageEntry | null): boolean {
  if (!page) return false;
  if (Array.isArray(page.blocks) && page.blocks.length > 0) return true;
  return String(page.content ?? '').trim().length > 0;
}

async function fetchStaticPageFromApi(qs: URLSearchParams): Promise<StaticPageEntry | null> {
  const url = `${getCmsBaseUrl()}/api/static-pages?${qs.toString()}`;
  const response = await fetch(url, buildCmsFetchOptions());
  if (!response.ok) return null;
  const json = (await response.json()) as Record<string, unknown>;
  const first = strapiDataArray(json)[0];
  if (!first) return null;
  return normalizeEntry(ensureRecord(first) as StrapiResponse<StaticPageEntry>['data'][number]);
}

export async function fetchStaticPage(
  slug: string,
  locale?: string,
): Promise<StaticPageEntry | null> {
  try {
    const normalizedSlug = String(slug ?? '').trim();
    if (!normalizedSlug) return null;

    const loc = String(locale ?? '').trim().toLowerCase();
    const isDefaultLocale = !loc || loc === DEFAULT_LOCALE;

    if (!isDefaultLocale) {
      const localized = await fetchStaticPageFromApi(
        buildStaticPageQueryParams(normalizedSlug, loc),
      );
      if (staticPageHasRenderableContent(localized)) {
        return localized;
      }
    }

    const english = await fetchStaticPageFromApi(
      buildStaticPageQueryParams(normalizedSlug, DEFAULT_LOCALE),
    );
    if (english) return english;

    if (isDefaultLocale) return null;

    // Last resort: Strapi default-locale query without explicit locale param.
    return await fetchStaticPageFromApi(buildStaticPageQueryParams(normalizedSlug, null));
  } catch {
    return null;
  }
}

function buildCmsFetchOptions(): RequestInit {
  const headers: Record<string, string> = {};
  const token = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return { next: { revalidate: 120 }, ...(Object.keys(headers).length ? { headers } : {}) };
}

export async function fetchCasinosFromCms(params: {
  q?: string;
  country?: string;
  license?: string;
  paymentMethods?: string[];
  maxDeposit?: number;
  minRating?: number;
  page: number;
  pageSize: number;
  locale?: string;
}): Promise<{ items: CmsCasino[]; total: number; page: number; pageSize: number; pageCount: number }> {
  const baseUrl = getCmsBaseUrl();
  const fetchOpts = buildCmsFetchOptions();

  // Build minimal query first - Strapi 5 i18n + fields/populate can cause empty results
  const buildQs = (minimal = false) => {
    const qs = new URLSearchParams();
    qs.set('pagination[page]', String(params.page));
    qs.set('pagination[pageSize]', String(params.pageSize));
    qs.set('sort', 'rating:desc');
    // Omit locale – Document Service returns empty when locale has no variants; show default locale casinos

    if (!minimal) {
      qs.set('populate[logo][fields][0]', 'url');
      qs.set('populate[paymentMethods][fields][0]', 'name');
      qs.set('populate[licenses][fields][0]', 'name');
      const q = String(params.q ?? '').trim();
      if (q) {
        qs.set('filters[$or][0][name][$containsi]', q);
        qs.set('filters[$or][1][description][$containsi]', q);
        qs.set('filters[$or][2][shortDescription][$containsi]', q);
      }
      const country = String(params.country ?? '').trim();
      if (country) qs.set('filters[country][name][$eq]', country);
      const license = String(params.license ?? '').trim();
      if (license) qs.set('filters[licenses][name][$containsi]', license);
      if (typeof params.maxDeposit === 'number' && Number.isFinite(params.maxDeposit)) {
        qs.set('filters[minDeposit][$lte]', String(params.maxDeposit));
      }
      if (typeof params.minRating === 'number' && Number.isFinite(params.minRating)) {
        qs.set('filters[rating][$gte]', String(params.minRating));
      }
      if (params.paymentMethods?.length) {
        params.paymentMethods.forEach((method, idx) => {
          const value = String(method ?? '').trim();
          if (value) qs.set(`filters[paymentMethods][name][$in][${idx}]`, value);
        });
      }
    }
    return qs;
  };

  const mainUrl = `${baseUrl}/api/casinos?${buildQs(false).toString()}`;
  try {
    const response = await fetch(mainUrl, fetchOpts);
    let json: StrapiFindResponse<CmsCasino> | null = null;
    if (response.ok) {
      json = (await response.json()) as StrapiFindResponse<CmsCasino>;
    }
    if (!response.ok || !json) {
      return { items: [], total: 0, page: params.page, pageSize: params.pageSize, pageCount: 1 };
    }
    let items = extractCasinoItems(json);
    items = items.filter((c) => c.isActive !== false);
    const pagination = extractCasinoPagination(json);
    return {
      items,
      total: pagination?.total ?? items.length,
      page: pagination?.page ?? params.page,
      pageSize: pagination?.pageSize ?? params.pageSize,
      pageCount:
        pagination?.pageCount ??
        Math.max(1, Math.ceil((pagination?.total ?? items.length) / params.pageSize)),
    };
  } catch {
    return {
      items: [],
      total: 0,
      page: params.page,
      pageSize: params.pageSize,
      pageCount: 1,
    };
  }
}

export async function fetchCasinoBySlugFromCms(slug: string, locale?: string): Promise<CmsCasino | null> {
  const qs = new URLSearchParams();
  qs.set('filters[slug][$eq]', slug);
  qs.set('pagination[pageSize]', '1');
  const loc = String(locale ?? '').trim().toLowerCase();
  if (loc && ['en', 'fr', 'es', 'ru'].includes(loc)) qs.set('locale', loc);
  // Detail view needs full relations (otherwise UI misses properties).
  // Using nested populate to ensure media fields are loaded properly
  qs.set('populate[logo]', 'true');
  qs.set('populate[banner]', 'true');
  qs.set('populate[screenshots]', 'true');
  qs.set('populate[country]', 'true');
  qs.set('populate[softwareProviders]', 'true');
  qs.set('populate[paymentMethods]', 'true');
  qs.set('populate[withdrawalMethods]', 'true');
  qs.set('populate[casinoTypes]', 'true');
  qs.set('populate[languages]', 'true');
  qs.set('populate[currencies]', 'true');
  qs.set('populate[licenses]', 'true');
  qs.set('populate[vipProgram]', 'true');
  qs.set('populate[bestForPlayersFrom]', 'true');
  qs.set('populate[pros]', 'true');
  qs.set('populate[cons]', 'true');

  const url = `${getCmsBaseUrl()}/api/casinos?${qs.toString()}`;
  try {
    let response = await fetch(url, { next: { revalidate: 60 } });
    if (!response.ok) return null;
    let json = (await response.json()) as StrapiFindResponse<CmsCasino>;
    let items = extractCasinoItems(json);
    if (items.length === 0 && loc && loc !== DEFAULT_LOCALE) {
      const fallbackQs = withEnglishLocale(qs);
      response = await fetch(`${getCmsBaseUrl()}/api/casinos?${fallbackQs.toString()}`, { next: { revalidate: 60 } });
      if (response.ok) {
        json = (await response.json()) as StrapiFindResponse<CmsCasino>;
        items = extractCasinoItems(json);
      }
    }
    return items[0] ?? null;
  } catch {
    return null;
  }
}
